import DateRangePicker from "components/common/DateRangePicker";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import Spinners from "components/common/Spinners";
import {
  formatDate,
  getAgeFromDOB,
  getMinMaxDate,
} from "components/common/Utils";
import {
  genderOptions,
  orderSpaceType,
  userRoleType,
} from "components/constants/DropdownOptions";
import { ArrowDown, ArrowUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import RevenueChart from "./RevenueChart";
import CustomerRequests from "./CustomerRequests";
import CurrentRewardLevel from "./CurrentRewardLevel";
import Wallet from "./Wallet";
import WalletRecharge from "./WalletRecharge";
import BookingHistory from "./BookingHistory";
import {
  getCustomerChartDetails,
  getCustomerDetails,
} from "store/actions/customerSlice";
import { useSearchParams } from "react-router-dom";
import IsLoading from "components/common/IsLoading";
import Riyal from "assets/images/icons/riyal.svg";
import { planOptions } from "components/constants/DropdownOptions";
import { Dropdown } from "primereact/dropdown";
import NoDataFound from "components/common/NoDataFound";
import { orderStatusTypes } from "components/constants/StatusKeys";
import Chair from "assets/images/icons/chair.svg";

const CustomerDetails = () => {
  const { t } = useTranslation();
  document.title = t("Customer Details | Ashjar Space");
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [selectedTab, setSelectedTab] = useState(1);
  const { loading, customerDetails, customerChartDetails, loading3 } =
    useSelector((state) => state.Customers);
  const [planType, setPlanType] = useState("");
  const [planDetails, setPlanDetails] = useState([]);
  const [dates, setDates] = useState([]);

  useEffect(() => {
    dispatch(getCustomerDetails({ id }));
    getChartData();
  }, []);

  useEffect(() => {
    setPlanDetails(customerDetails?.planDetails?.activePlans);
  }, [customerDetails]);

  const getChartData = (action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const isReset = actionType === "RESET";

    const payload = {
      id,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      bookingType: "plan",
      planType: isReset ? "" : planType,
    };
    dispatch(getCustomerChartDetails(payload));
  };

  const handleReset = () => {
    setDates([]);
    getChartData({ actionType: "RESET" });
    setPlanType("");
  };

  const { bookingBreakdown } = customerDetails || {};

  return (
    <React.Fragment>
      <div className="page-content common_details_page order_details customer_details">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Customer Information")} />
            </Row>
            <div className="common_wrapper">
              <Row className="top_row">
                <Col lg={6}>
                  <div className="common_card_inner order_customer_details">
                    <p className="fs_14 text_gray">{t("Name")}</p>
                    <h2 className="fs_20 text_black mb-2">
                      {customerDetails?.name}
                    </h2>
                    <div className="d-flex flex-wrap gap-5 mt-4">
                      <div className="inner_details">
                        <h2>{t("Gender")}</h2>
                        <h3>
                          {t(
                            genderOptions?.find(
                              (option) =>
                                option.value === customerDetails?.gender
                            )?.name
                          )}
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Age")}</h2>
                        <h3>
                          {getAgeFromDOB(customerDetails?.dob)},{" "}
                          <span className="text_gray">
                            {customerDetails?.dob}
                          </span>
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Status")}</h2>
                        <h3>
                          {t(
                            userRoleType?.find(
                              (option) =>
                                option.value === customerDetails?.roleType
                            )?.name
                          )}
                        </h3>
                      </div>
                      <div className="inner_details">
                        <h2>{t("Number")}</h2>
                        <h3>{customerDetails?.phone}</h3>
                      </div>
                    </div>
                    <div className="inner_details mt-4">
                      <h2>
                        {t("Member since")}{" "}
                        {formatDate(customerDetails?.createdAt)}
                      </h2>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="common_card_inner price_details">
                    <h2 className="fs_20 text_black mb-2">
                      {t("Customer Device Information")}
                    </h2>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Model")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.model || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Platform")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.platform || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Device Id")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.deviceId || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("App Version")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.appVersion || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("OS Version	")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.osVersion || "-"}
                      </h2>
                    </div>
                    <div className="g_between mt-2">
                      <h2 className="fs_14">{t("Language")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.deviceInfo?.language || "-"}
                      </h2>
                    </div>
                    {/* <div className="g_between mt-2 gap-3">
                      <h2 className="fs_14 w_fit_content">{t("FCM Token")}</h2>
                      <h2 className="fs_14">
                        {customerDetails?.userInfo?.deviceInfo?.fcmToken || "-"}
                      </h2>
                    </div> */}
                  </div>
                </Col>
                <Col lg={12}>
                  <Row className="revenue_row mt-2">
                    <Col lg={6}>
                      <Row>
                        <h2 className="fs_20 text_black mt-2">
                          {t("Booking Breakdown for Day")}
                        </h2>
                        <BookingCard
                          data={bookingBreakdown?.day?.workspace}
                          t={t}
                          title={"Workspace"}
                        />
                        <BookingCard
                          data={bookingBreakdown?.day?.meetingRoom}
                          t={t}
                          title={"Meeting Room"}
                        />
                        <BookingCard
                          data={bookingBreakdown?.day?.workshop}
                          t={t}
                          title={"Workshop"}
                        />
                        <BookingCard
                          data={bookingBreakdown?.day?.hotDesk}
                          t={t}
                          title={"Hot Desk"}
                        />
                      </Row>
                    </Col>
                    <Col lg={6}>
                      <Row>
                        <h2 className="fs_20 text_black mt-2">
                          {t("Booking Breakdown for Plan")}
                        </h2>

                        <Col md={12}>
                          <div className="common_card_inner">
                            <div className="inner_section">
                              <div className="g_between">
                                <h2 className="fs_20 text_black">
                                  {t("Plan")}
                                </h2>
                                <div className="total_bookings">
                                  <img
                                    src={Chair}
                                    alt="char"
                                    width={15}
                                    height={15}
                                  />
                                  <p className="fs_12 text_dark_gray">
                                    {t("Total Bookings")}:{" "}
                                    <span className="fs_14 text_black">
                                      {" "}
                                      {bookingBreakdown?.plan?.totalSummary
                                        ?.totalBookings ?? "0"}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <h3 className="fs_12 text_dark_gray">
                                {t("Total Revenue")}
                              </h3>
                              <h2 className="fs_40 text_black">
                                <img
                                  src={Riyal}
                                  width={25}
                                  height={27}
                                  alt="riyal"
                                  className="me-1"
                                />
                                {bookingBreakdown?.plan?.totalSummary?.totalRevenue?.toLocaleString(
                                  "en-US"
                                )}
                              </h2>
                              <Row>
                                <BookingCard2
                                  title="Workspace"
                                  data={bookingBreakdown?.plan?.workspace}
                                  t={t}
                                />
                                <BookingCard2
                                  title="Meeting Room"
                                  data={bookingBreakdown?.plan?.meetingRoom}
                                  t={t}
                                />
                                <BookingCard2
                                  title="Office"
                                  data={bookingBreakdown?.plan?.office}
                                  t={t}
                                />
                                <BookingCard2
                                  title="Hot Desk"
                                  data={bookingBreakdown?.plan?.hotDesk}
                                  t={t}
                                />
                              </Row>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={12}>
                  <div className="common_card_inner">
                    <div>
                      <div className="g_between">
                        <h2 className="fs_20">{t("Plans Revenue")}</h2>
                        <div className="d-flex gap-3 common_filter plan-chart">
                          <div className="buttons">
                            <button
                              className="search_btn"
                              onClick={() =>
                                getChartData({ actionType: "SEARCH" })
                              }
                            >
                              {t("Search")}
                            </button>
                            <button className="reset_btn" onClick={handleReset}>
                              {t("Reset")}
                            </button>
                          </div>
                          <Dropdown
                            key="planType"
                            value={planType}
                            onChange={(e) => {
                              setPlanType(e.value);
                            }}
                            options={planOptions}
                            optionLabel="name"
                            placeholder={t("Plan Type")}
                            className="custom_dropdown"
                          />
                          <DateRangePicker dates={dates} setDates={setDates} />
                        </div>
                      </div>
                      {loading3 ? (
                        <IsLoading />
                      ) : (
                        <>
                          <h3 className="fs_40">
                            <img
                              src={Riyal}
                              width={25}
                              height={25}
                              alt="riyal"
                              className="me-1"
                            />
                            {customerChartDetails?.chartData?.totalRevenue}
                          </h3>
                          <div className="d-flex align-items-center">
                            <div
                              className={`revenue_div ${
                                ["positive", "neutral"]?.includes(
                                  customerChartDetails?.todayVsYesterdayChangeType
                                )
                                  ? "positive"
                                  : "negative"
                              }`}
                            >
                              <span className="fs_14 text_primary">
                                {["positive", "neutral"]?.includes(
                                  customerChartDetails?.todayVsYesterdayChangeType
                                ) ? (
                                  <ArrowUp className="text_primary" size={18} />
                                ) : (
                                  <ArrowDown
                                    className="text_danger"
                                    size={18}
                                  />
                                )}
                                {customerChartDetails?.todayVsYesterdayChange}
                              </span>
                            </div>
                            <span className="fs_14 mx-2">
                              {t("Number of bookings")}{" "}
                              {customerChartDetails?.chartData?.totalBookings}
                            </span>
                          </div>
                          <RevenueChart
                            categories={
                              customerChartDetails?.chartData?.categories
                            }
                            data={customerChartDetails?.chartData?.data}
                            averageRevenue={
                              customerChartDetails?.chartData?.averageRevenue
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={12}>
                  <div className="common_card_inner">
                    <div>
                      <div className="d-flex gap-3 justify-content-left">
                        <h2 className="fs_20">{t("Plans")}</h2>

                        <div className="tab_btns">
                          <button
                            className={selectedTab === 1 ? "active" : ""}
                            onClick={() => {
                              setSelectedTab(1);
                              setPlanDetails(
                                customerDetails?.planDetails?.activePlans ?? []
                              );
                            }}
                          >
                            {t("Current Plan")}
                          </button>
                          <button
                            className={selectedTab === 2 ? "active" : ""}
                            onClick={() => {
                              setSelectedTab(2);
                              setPlanDetails(
                                customerDetails?.planDetails?.pastPlans ?? []
                              );
                            }}
                          >
                            {t("Previous Plan")}
                          </button>
                        </div>
                      </div>

                      {loading3 ? (
                        <IsLoading />
                      ) : (
                        <>
                          {planDetails?.length === 0 ? (
                            <NoDataFound />
                          ) : (
                            <div
                              className="px-2"
                              style={{ maxHeight: 400, overflowY: "auto" }}
                            >
                              {planDetails?.map((item) => (
                                <div className="d-flex align-items-center gap-3 w-100 plan-card mb-2">
                                  <img
                                    src={
                                      item?.space?.imageUrls[0] ??
                                      "https://ashjar-dev.s3.ap-south-1.amazonaws.com/ashjar-space/meetingRoom/1744464931094-3.png"
                                    }
                                    width="59"
                                    height="55"
                                    className="card-image"
                                  />

                                  <div>
                                    <h2 className="fs_12 text_black text-capitalize">
                                      {item?.space?.name} (
                                      {item?.space?.spaceType})
                                    </h2>
                                    <h2 className="fs_12 text_black text-capitalize">
                                      ({item?.planDetails?.planType} Plan -{" "}
                                      {item?.planDetails?.duration}
                                      {item?.planDetails?.planType === "monthly"
                                        ? " Month"
                                        : ""}
                                      {item?.planDetails?.planType === "yearly"
                                        ? " Year"
                                        : ""}
                                      {item?.planDetails?.planType === "daily"
                                        ? " Day"
                                        : ""}
                                      )
                                    </h2>
                                    <h2 className="fs_12 text_gray mt-1">
                                      <span>{item?.city?.name}, </span>
                                      <span>{item?.branch?.name}</span>
                                    </h2>
                                    <h2 className="fs_12 text_gray">
                                      {formatDate(item?.planDetails?.startDate)}{" "}
                                      - {formatDate(item?.planDetails?.endDate)}
                                    </h2>
                                  </div>

                                  <div className="amount-div">
                                    <h6 className="fs_14">
                                      {" "}
                                      <img
                                        src={Riyal}
                                        width={12}
                                        height={12}
                                        alt="riyal"
                                        className="me-1 mb-1"
                                      />
                                      {item?.totalAmount}
                                    </h6>
                                    <p
                                      className={`w_fit_content mt-2 ${
                                        orderStatusTypes?.find(
                                          (option) =>
                                            option.value === item.bookingStatus
                                        )?.class
                                      }`}
                                    >
                                      {t(
                                        orderStatusTypes?.find(
                                          (option) =>
                                            option.value === item.bookingStatus
                                        )?.name
                                      ) || "-"}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={4}>
                  <CustomerRequests
                    id={id}
                    customerReq={customerDetails?.workshopRequests}
                  />
                </Col>
                <Col lg={8}>
                  <CurrentRewardLevel
                    id={id}
                    cashbackLevels={customerDetails?.cashbackLevels}
                    walletInfo={customerDetails?.walletInfo}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col lg={8}>
                  <Wallet id={id} />
                </Col>
                <Col lg={4}>
                  <WalletRecharge id={id} />{" "}
                </Col>
              </Row>
            </div>
            <Row className="mt-5">
              <DetailsPageTitle
                title={t("Booking History")}
                isBackBtn={false}
              />
              <div className="common_wrapper">
                <BookingHistory id={id} />
              </div>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default CustomerDetails;

const BookingCard = ({ data, t, title }) => (
  <Col lg={6}>
    <div className="common_card_inner">
      <h2 className="fs_12 text_gray">{t("Total Revenue")}</h2>
      <h2 className="fs_40 d-flex align-items-center">
        <img src={Riyal} width={30} height={30} alt="riyal" className="me-1" />
        {data?.totalRevenue?.toLocaleString("en-US") ?? "00"}
      </h2>
      <h2 className="fs_20">{t(title)}</h2>
      <p className="fs_14 text_gray mt-1">
        {t("Total Bookings")}:{" "}
        <span className="text_primary">{data?.totalBookings ?? "00"}</span>
      </p>
    </div>
  </Col>
);
const BookingCard2 = ({ title, data, t }) => (
  <Col lg={6}>
    <div className="customer_plan_card">
      <div className="single_card">
        <h2 className="fs_14 text_black">{t(title)}</h2>
        <div className="d-flex align-items-center gap-2">
          <h2 className="fs_12 text_dark_gray">{t("Total Bookings")}:</h2>
          <h2 className="fs_14 text_black">
            {data?.totalBookings?.toLocaleString("en-US")}
          </h2>
        </div>
        <div className="d-flex align-items-center gap-2 ">
          <h2 className="fs_12 text_dark_gray">{t("Total Revenue")}:</h2>
          <div className="d-flex align-items-center gap-1">
            <img src={Riyal} width={10} height={11} alt="" />
            <h2 className="fs_14 text_black">
              {data?.totalRevenue?.toLocaleString("en-US")}
            </h2>
          </div>
        </div>
      </div>
    </div>
  </Col>
);
